import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Identidad del PRODUCTO: Pulso by Gelux (ADR-013).
 *
 * Es la marca de quien vende el expediente, la misma de GestionHospitalaria,
 * no la de un consultorio. Vive solo en esta carpeta. La identidad de cada
 * consultorio —nombre, logo, colores, membrete— sigue saliendo de
 * `Organizacion.marca` (regla 3), y nada de esto llega a un documento clinico:
 * informes, recetas y notas se imprimen con el membrete del consultorio.
 */
export const PULSO = {
  nombre: 'Pulso',
  fabricante: 'by Gelux',
  /**
   * Primario y secundario de la paleta clinica de Pulso (#064E3B, PANTONE
   * 3435 C; #F8E7C9, PANTONE 7499 C aprox.). Solo pintan el acento mientras
   * no se conoce el consultorio: el acceso general y la consola de plataforma.
   */
  colorPrimario: '#064e3b',
  colorSecundario: '#f8e7c9',
} as const;

/** «Pulso by Gelux», para textos alternativos y pies. */
export const FIRMA_DE_PULSO = `${PULSO.nombre} ${PULSO.fabricante}`;

/**
 * Armazon de Pulso: una superficie en el pino de la marca con el destello del
 * pulso (barra lateral, cabecera de la consola y del portal). No pinta
 * componentes uno a uno: reasigna los roles de texto, borde, hover, seleccion
 * y foco, para que lo que vive dentro los lea ya en su version sobre la noche.
 * Igual en claro y en oscuro, como el logo. Equivale a `.brand-night` de
 * GestionHospitalaria.
 */
export const ARMAZON_PULSO: SxProps<Theme> = {
  '--text-primary': 'var(--text-on-brand)',
  '--text-secondary': 'var(--text-on-brand-muted)',
  '--text-tertiary': 'var(--text-on-brand-subtle)',
  '--border-hairline': 'var(--bg-on-brand-overlay)',
  '--border-subtle': 'var(--bg-on-brand-overlay)',
  '--bg-hover': 'var(--bg-on-brand-hover)',
  '--bg-selected': 'var(--bg-on-brand-selected)',
  '--bg-raised': 'var(--bg-on-brand-selected)',
  '--bg-chrome': 'var(--brand-night-bg)',
  '--accent-text': 'var(--brand-pulse-mid)',
  '--accent-emphasis': 'var(--brand-pulse-mid)',
  // `--focus-ring` se resuelve en `:root`: hay que redeclararlo, no basta con
  // cambiar el color del que depende.
  '--focus-color': 'var(--brand-pulse-mid)',
  '--focus-ring': '0 0 0 2px var(--brand-night-bg), 0 0 0 4px var(--brand-pulse-mid)',
  background:
    'radial-gradient(70% 35% at 0% 0%, color-mix(in oklch, var(--brand-pulse-start) 10%, transparent), transparent 70%), linear-gradient(170deg, var(--brand-night-bg), var(--brand-night-bg-2))',
  color: 'var(--text-on-brand)',
};

/** Ficha de iniciales en el trazo del pulso: verde a cian, letra en la noche. */
export const FICHA_DE_PULSO: SxProps<Theme> = {
  background: 'linear-gradient(135deg, var(--brand-pulse-start), var(--brand-pulse-end))',
  color: 'var(--text-on-brand-pulse)',
};
